import { FC, forwardRef, PropsWithoutRef, ReactText, Ref } from 'react';
import styled, { css } from 'styled-components';
import { rgba } from 'polished';

import { calculateFontSize } from '../../styles';
import { defaultThemeProp } from '../../theme';
import { ForwardProps } from '../../types';
import { tryCatch } from '../../utils';

export interface StatusProps {
  /** Determines the color to render the Badge as. This color is derived from the Theme. */
  variant: 'success' | 'urgent' | 'warn' | 'pending' | 'info';
  /** The content of the Badge, transformed to uppercase. */
  children: ReactText;
}

export const StyledStatus = styled.span<StatusProps>(props => {
  const { spacing } = props.theme.base;
  const borderRadius = props.theme.base['border-radius'];
  const { status } = props.theme.components.badges;

  const { background, foreground } = status[props.variant];
  const shadow = tryCatch(() => rgba(foreground, 0.1));
  const fontSize = calculateFontSize(props.theme.base['font-size'], props.theme.base['font-scale']);

  return css`
    background-color: ${background};
    border-radius: calc(0.25 * ${borderRadius});
    color: ${foreground};
    box-shadow: inset 0 0 0 0.0625rem ${shadow};
    display: inline-block;
    font-size: ${fontSize.xxs};
    font-weight: bold;
    line-height: calc(${spacing} * 2.5);
    padding: 0 ${spacing};
    text-transform: uppercase;
  `;
});

StyledStatus.defaultProps = defaultThemeProp;

const Status: FC<StatusProps & ForwardProps> = forwardRef(
  (
    { variant, children, ...restProps }: PropsWithoutRef<StatusProps>,
    ref: Ref<HTMLSpanElement>
  ) => {
    return (
      <StyledStatus {...restProps} variant={variant} ref={ref}>
        {children}
      </StyledStatus>
    );
  }
);

Status.defaultProps = {
  variant: 'info'
};

export default Status;
